/**
 * Utility functions for file operations
 */

/**
 * Converts a File object to a Base64 encoded string
 *
 * @param file The File object to convert
 * @returns A Promise that resolves with the Base64 encoded string
 */
export const convertFileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64String = reader.result as string;
      // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
      const base64Content = base64String.split(',')[1];
      resolve(base64Content);
    };
    reader.onerror = error => {
      reject(error);
    };
  });
};

export const convertFileToBase64WithFileName = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64String = reader.result as string;
      // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
      const base64Content = base64String.split(',')[1];
      // Create a JSON string containing both base64 and file name
      const result = JSON.stringify({
        base64: base64Content,
        fileName: file.name,
      });
      resolve(result);
    };
    reader.onerror = error => {
      reject(error);
    };
  });
};

export interface FileData {
  base64: string;
  fileName: string;
}
export const parseFileData = (data: string | undefined): FileData | null => {
  if (!data) return null;
  try {
    return JSON.parse(data) as FileData;
  } catch {
    return null;
  }
};

/**
 * Creates a download link for a blob and triggers download
 *
 * @param blob The blob to download
 * @param filename The name to give the downloaded file
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
  // Create a URL for the blob
  const url = window.URL.createObjectURL(blob);

  // Create a link element
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);

  // Append to document, click, and clean up
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Release the blob URL
  window.URL.revokeObjectURL(url);
};

/**
 * Extracts filename from Content-Disposition header
 *
 * @param contentDisposition The Content-Disposition header value
 * @param defaultFilename Default filename to use if extraction fails
 * @returns The extracted filename or the default
 */
export const getFilenameFromContentDisposition = (contentDisposition: string | null | undefined, defaultFilename = 'download'): string => {
  if (!contentDisposition) {
    return defaultFilename;
  }

  const filenameMatch = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
  if (filenameMatch && filenameMatch[1]) {
    return filenameMatch[1].replace(/['"]/g, '');
  }

  return defaultFilename;
};

export const sanitizeFileName = name => {
  return name.replace(/[^a-zA-Z0-9-_]/g, '-');
};
